import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { PortalClientSecurityRight, SecurityRoleTiny, SecurityRoleType } from '@jms/model';
import { useState } from '@jms/shared/ngrx-actions';
import { Store } from '@ngrx/store';
import { first, map, skipWhile } from 'rxjs/operators';

export enum clientPaths {
    Home = 'home',
    Operations = 'operations',
    Quotes = 'quotes',
    Invoices = 'invoices',
    Sites = 'sites',
    Services = 'services',
    WorkTaskSchedule = 'work-task-schedule',
    Files = 'files',

    Staff = 'staff',
    StaffInvites = 'staff/invites',
    StaffContacts = 'staff/contacts',

    Invites = 'invites',
    Logins = 'logins',
    Contacts = 'contacts',

    Settings = 'settings',
}

@Injectable({ providedIn: 'root' })
export class PortalClientAuthorizeGuard {

    private $role = useState<SecurityRoleTiny>('application.activeRole');
    private readonly store = inject(Store);

    /**
     * Route activation. Angular runs this BEFORE the parent resolver, so a hard refresh of
     * `/services` would otherwise see `activeRole: null`, return false, and fall through to home.
     * Wait for the role the entities call is already fetching, then decide.
     */
    public canLoad(route: ActivatedRouteSnapshot) {

        return this.store
            .select(state => (state as { application?: { activeRole?: SecurityRoleTiny } }).application?.activeRole)
            .pipe(
                skipWhile(role => role == null),
                first(),
                map(role => this.allowsPath(route.routeConfig.path, role)),
            );
    }

    public canLoadPath(path: string) {

        return this.allowsPath(path, this.$role());
    }

    private allowsPath(path: string, role: SecurityRoleTiny | null | undefined) {

        if (!role) return false;

        if (role.name === SecurityRoleType.SystemAdministrator) return true;

        const { rights } = role;

        if (!rights?.length) return false;

        switch (path) {

            case clientPaths.Home:
                return true;

            case clientPaths.Operations:
                return rights.includes(PortalClientSecurityRight.CanAccessJobs);

            case clientPaths.Quotes:
                return rights.includes(PortalClientSecurityRight.CanAccessQuotes);

            case clientPaths.Invoices:
                return rights.includes(PortalClientSecurityRight.ViewInvoice);

            case clientPaths.Sites:
                return rights.includes(PortalClientSecurityRight.CanAccessSites);

            // Upcoming and History are one tab over the same services, so one path and one right.
            case clientPaths.Services:
            case clientPaths.WorkTaskSchedule:
                return rights.includes(PortalClientSecurityRight.CanAccessServices);

            case clientPaths.Files:
                return rights.includes(PortalClientSecurityRight.CanAccessFiles);

            case clientPaths.Staff:
            case clientPaths.StaffContacts:
            case clientPaths.StaffInvites:
            case clientPaths.Logins:
            case clientPaths.Contacts:
            case clientPaths.Invites:
                return rights.includes(PortalClientSecurityRight.ManageUsers);

            case clientPaths.Settings:
                return true;

            default:
                return false;
        }
    }
}
